import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { ReportsService } from '../reports.service';
import { ReportsRepository } from '@/store/reports-state/reports-state.repository';
import { DacService } from '../../dac/dac.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { getCreateReportsFormObject } from '../utils/reports-form-object';
import { ReportAdditifRequest } from '@/types';
import { ButtonModule } from 'primeng/button';
import { reportsSchema } from '@/validations/reports.validation';

@Component({
  selector: 'sm-create-reports',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent, FormBuilderComponent, TranslateModule, ButtonModule],
  templateUrl: './create-reports.component.html',
  styleUrl: './create-reports.component.scss',
})
export class CreateReportsComponent implements OnInit {
  reportsService = inject(ReportsService);
  reportsRepository = inject(ReportsRepository);
  dacService = inject(DacService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  breadcrumb = [
    {
      label: 'settings.paymentJustification.breadcrumb.settings',
      routerLink: ['/settings'],
    },
    {
      label: 'clarification.reports.plural',
      routerLink: ['/reports'],
    },
    {
      label: 'clarification.reports.create_title',
      routerLink: ['/reports/create'],
    },
  ];

  formObject?: DynamicForm;
  dacOptions$ = new BehaviorSubject<SelectOption[]>([]);

  stableDacId$ = this.reportsRepository.getNestedObservable<number>('createRequest', 'dacId');
  stableSourceDemande$ = this.reportsRepository.getNestedObservable<string>('createRequest', 'sourceDemande');

  showDemandeReports$ = combineLatest([
    this.stableDacId$,
    this.stableSourceDemande$,
  ]).pipe(
    map(([dacId, source]) => !!dacId && source === 'DEMANDE_CANDIDATS')
  );

  demandeReports$ = combineLatest([
    this.stableDacId$,
    this.stableSourceDemande$,
  ]).pipe(
    switchMap(([dacId, source]) => {
      if (dacId && source === 'DEMANDE_CANDIDATS') {
        return this.reportsService.getDemandeReportsByDac(dacId);
      }
      return of([]);
    })
  );

  isFormValid$ = combineLatest([
    this.reportsRepository.getNestedObservable<number>('createRequest', 'dacId'),
    this.reportsRepository.getNestedObservable<Date>('createRequest', 'dateReport'),
    this.reportsRepository.getNestedObservable<string>('createRequest', 'motifReport'),
    this.reportsRepository.getNestedObservable<string>('createRequest', 'sourceDemande'),
  ]).pipe(
    map(([dacId, dateReport, motifReport, sourceDemande]) => {
      const result = reportsSchema.safeParse({ dacId, dateReport, motifReport, sourceDemande });
      return result.success;
    })
  );

  ngOnInit(): void {
    this.reportsRepository.reset();
    this.initForm();
    this.loadInitialData();
  }

  initForm() {
    this.formObject = getCreateReportsFormObject({
      store: this.reportsRepository,
      dacOptions: this.dacOptions$,
      demandeReports$: this.demandeReports$,
      showDemandeReports$: this.showDemandeReports$,
    });
  }

  loadInitialData() {
    this.dacService.getAllPublishedDacsSoftForReportRequests().subscribe({
      next: (response: any) => {
        this.reportsRepository.set('availableDacs', response);
        const options = response.map((dac: any) => ({
          name: dac.referenceDac,
          code: dac.dacId,
        }));
        this.dacOptions$.next(options);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: 'Impossible de charger les DACs',
        });
      },
    });
  }

  onFieldBlur() {
    // This is called when fields lose focus, as seen in add-committee.component
  }

  save() {
    this.reportsRepository.set('formSubmitted', true);

    const request = { ...this.reportsRepository.get<ReportAdditifRequest>('createRequest') };
    
    if (request.dateReport) {
      const d = new Date(request.dateReport);
      request.dateReport = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    }
    
    const result = reportsSchema.safeParse(request);

    if (!result.success) {
      // Mapping Zod errors to state errors if BaseStoreRepository supports it
      this.reportsRepository.setErrors(result.error.flatten().fieldErrors as any);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('common.validationError'),
      });
      return;
    }

    this.reportsService.createReport(request).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.success'),
          detail: 'Report créé avec succès',
        });
        this.router.navigate(['/report-request']);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.error'),
          detail: err.error?.message || 'Erreur lors de la création du report',
        });
      },
    });
  }

  cancel() {
    this.router.navigate(['/reports']);
  }
}
